import React from 'react';
import { Search, MoreVertical, Send, Phone, Video, ArrowLeft, Image, Mic, Smile, PlusCircle, MessageSquare, Users, Bell, Filter, Pin, Star, CheckCheck, Clock, User } from 'lucide-react';
import { AnonymousIcon } from './AnonymousIcon';
import { useMediaQuery } from '../hooks/useMediaQuery';

interface ChatMessage {
  id: string;
  content: string;
  sender: string;
  senderAvatar?: string;
  timestamp: string;
  isOwn: boolean;
  isRead: boolean;
}

interface ChatConversation {
  id: string;
  name: string;
  avatar?: string;
  lastMessage: string;
  timestamp: string;
  unreadCount: number;
  isGroup: boolean;
  isOnline?: boolean;
  isPinned?: boolean;
}

function ConversationItem({
  conversation,
  isActive,
  onClick,
}: {
  conversation: ChatConversation;
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <div
      onClick={onClick}
      className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
        isActive ? 'bg-blue-600/20' : 'hover:bg-gray-800/50'
      }`}
    >
      <div className="relative">
        {conversation.avatar ? (
          <img
            src={conversation.avatar}
            alt={conversation.name}
            className="w-12 h-12 rounded-full object-cover"
          />
        ) : (
          <AnonymousIcon seed={conversation.id} className="w-12 h-12" />
        )}
        {conversation.isOnline && (
          <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-black"></div>
        )}
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <p className="font-medium text-white truncate">
            {conversation.name}
            {conversation.isPinned && <Pin className="w-3 h-3 ml-1 inline text-blue-400" />}
          </p>
          <span className="text-xs text-gray-400 whitespace-nowrap">{conversation.timestamp}</span>
        </div>
        <div className="flex items-center justify-between mt-1">
          <p className="text-sm text-gray-400 truncate">{conversation.lastMessage}</p>
          {conversation.unreadCount > 0 && (
            <span className="bg-blue-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
              {conversation.unreadCount}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function MessageBubble({ message }: { message: ChatMessage }) {
  return (
    <div className={`flex gap-2 mb-4 ${message.isOwn ? 'justify-end' : 'justify-start'}`}>
      {!message.isOwn && (
        <div className="flex-shrink-0 mt-1">
          {message.senderAvatar ? (
            <img
              src={message.senderAvatar}
              alt={message.sender}
              className="w-8 h-8 rounded-full"
            />
          ) : (
            <AnonymousIcon seed={message.sender} className="w-8 h-8" />
          )}
        </div>
      )}
      
      <div className={`max-w-[75%] ${message.isOwn ? 'items-end' : 'items-start'}`}>
        {!message.isOwn && (
          <p className="text-gray-400 text-xs mb-1">{message.sender}</p>
        )}
        <div className={`rounded-2xl px-4 py-2 ${
          message.isOwn 
            ? 'bg-blue-600 text-white rounded-br-sm' 
            : 'bg-gray-800 text-white rounded-bl-sm'
        }`}>
          <p className="whitespace-pre-wrap">{message.content}</p>
        </div>
        <div className="flex items-center gap-1 mt-1">
          <span className="text-gray-500 text-xs">{message.timestamp}</span>
          {message.isOwn && (
            <CheckCheck className={`w-3 h-3 ${message.isRead ? 'text-blue-400' : 'text-gray-500'}`} />
          )}
        </div>
      </div>
    </div>
  );
}

function ChatHeader({ 
  conversation,
  onBackClick,
  isMobile
}: { 
  conversation: ChatConversation | null;
  onBackClick: () => void;
  isMobile: boolean;
}) {
  if (!conversation) return null;
  
  return (
    <div className="flex items-center justify-between p-4 border-b border-gray-800">
      <div className="flex items-center gap-3">
        {isMobile && (
          <button 
            onClick={onBackClick}
            className="p-1 hover:bg-gray-800 rounded-full transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
        )}
        
        {conversation.avatar ? (
          <img
            src={conversation.avatar}
            alt={conversation.name}
            className="w-10 h-10 rounded-full"
          />
        ) : (
          <AnonymousIcon seed={conversation.id} className="w-10 h-10" />
        )}
        
        <div>
          <p className="font-medium text-white">{conversation.name}</p>
          <p className="text-xs text-gray-400">
            {conversation.isOnline ? 'Online now' : 'Last seen recently'}
          </p>
        </div>
      </div>
      
      <div className="flex items-center gap-3">
        <button className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-full transition-colors">
          <Phone className="w-5 h-5" />
        </button>
        <button className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-full transition-colors">
          <Video className="w-5 h-5" />
        </button>
        <button className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-full transition-colors">
          <MoreVertical className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}

function ChatInput({ onSendMessage }: { onSendMessage: (message: string) => void }) {
  const [message, setMessage] = React.useState('');
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (message.trim()) {
      onSendMessage(message);
      setMessage('');
    }
  };
  
  return (
    <form onSubmit={handleSubmit} className="border-t border-gray-800 p-3">
      <div className="flex items-center gap-2 bg-gray-800/50 rounded-full px-4 py-2">
        <button 
          type="button" 
          className="text-gray-400 hover:text-white transition-colors"
        >
          <PlusCircle className="w-6 h-6" />
        </button>
        
        <input
          type="text"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Type a message..."
          className="flex-1 bg-transparent text-white placeholder-gray-400 outline-none min-h-[24px]"
        />
        
        <div className="flex items-center gap-2">
          <button 
            type="button"
            className="text-gray-400 hover:text-white transition-colors"
          >
            <Image className="w-5 h-5" />
          </button>
          <button 
            type="button"
            className="text-gray-400 hover:text-white transition-colors"
          >
            <Mic className="w-5 h-5" />
          </button>
          <button 
            type="button"
            className="text-gray-400 hover:text-white transition-colors"
          >
            <Smile className="w-5 h-5" />
          </button>
          <button
            type="submit"
            disabled={!message.trim()}
            className={`p-2 rounded-full ${
              message.trim() ? 'bg-blue-600 text-white hover:bg-blue-500' : 'bg-gray-700 text-gray-400'
            } transition-colors`}
          >
            <Send className="w-5 h-5" />
          </button>
        </div>
      </div>
    </form>
  );
}

function EmptyChatState() {
  return (
    <div className="flex flex-col items-center justify-center h-full p-8 text-center">
      <div className="bg-gray-800/50 p-4 rounded-full mb-4">
        <MessageSquare className="w-12 h-12 text-gray-500" />
      </div>
      <h3 className="text-white text-lg font-semibold mb-2">No conversation selected</h3>
      <p className="text-gray-400 text-sm max-w-md mb-6">
        Select a conversation from the list or start a new chat to begin messaging
      </p>
      <button className="bg-blue-600 hover:bg-blue-500 text-white px-6 py-3 rounded-full text-sm font-semibold transition-colors flex items-center gap-2">
        <User className="w-5 h-5" />
        <span>Start New Chat</span>
      </button>
    </div>
  );
}

function ChatSidebar({
  conversations,
  activeConversationId,
  onConversationSelect,
  searchTerm,
  onSearchChange,
  onFilterToggle,
  isMobile,
}) {
  const filteredConversations = React.useMemo(() => {
    if (!searchTerm) return conversations;
    const term = searchTerm.toLowerCase();
    return conversations.filter(
      convo => convo.name.toLowerCase().includes(term) || convo.lastMessage.toLowerCase().includes(term)
    );
  }, [conversations, searchTerm]);

  return (
    <div className={`flex flex-col h-full ${isMobile ? 'w-full' : 'w-80 border-r border-gray-800'}`}>
      <div className="p-4 border-b border-gray-800">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-white text-xl font-bold">Messages</h2>
          <div className="flex items-center gap-2">
            <button className="p-2 text-gray-400 hover:text-white hover:bg-gray-800/50 rounded-full transition-colors">
              <Filter className="w-5 h-5" onClick={onFilterToggle} />
            </button>
            <button className="p-2 text-gray-400 hover:text-white hover:bg-gray-800/50 rounded-full transition-colors">
              <Bell className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        <div className="flex items-center gap-2 bg-gray-800/50 rounded-full pl-3 pr-1 py-1">
          <Search className="w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search messages"
            className="flex-1 bg-transparent text-white placeholder-gray-400 outline-none"
          />
          {searchTerm && (
            <button
              onClick={() => onSearchChange('')}
              className="bg-gray-700 p-1 rounded-full text-gray-400 hover:text-white hover:bg-gray-600 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      
      <div className="flex-1 overflow-y-auto custom-scrollbar">
        <div className="p-2">
          <div className="flex items-center gap-3 mb-3 px-2">
            <button className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg bg-gray-800/50 text-white hover:bg-gray-700 transition-colors">
              <MessageSquare className="w-4 h-4" />
              <span className="text-sm">Chats</span>
            </button>
            <button className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg bg-gray-800/30 text-gray-400 hover:bg-gray-800/50 transition-colors">
              <Users className="w-4 h-4" />
              <span className="text-sm">Groups</span>
            </button>
          </div>
          
          {filteredConversations.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
              <Search className="w-10 h-10 text-gray-500 mb-3" />
              <p className="text-gray-400 text-sm">
                No conversations found matching '{searchTerm}'
              </p>
            </div>
          ) : (
            filteredConversations.map(conversation => (
              <ConversationItem
                key={conversation.id}
                conversation={conversation}
                isActive={activeConversationId === conversation.id}
                onClick={() => onConversationSelect(conversation.id)}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export function ChatView() {
  const [conversations, setConversations] = React.useState<ChatConversation[]>([
    {
      id: 'conv1',
      name: 'Sarah Johnson',
      avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150',
      lastMessage: 'Did you see the latest news clip?',
      timestamp: '10:30 AM',
      unreadCount: 2,
      isGroup: false,
      isOnline: true,
      isPinned: true
    },
    {
      id: 'conv2',
      name: 'News Enthusiasts',
      lastMessage: 'James: This is incredible footage',
      timestamp: 'Yesterday',
      unreadCount: 0,
      isGroup: true
    },
    {
      id: 'conv3',
      name: 'Michael Chen',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150',
      lastMessage: 'Looking forward to your next upload',
      timestamp: 'Yesterday',
      unreadCount: 0,
      isGroup: false,
      isOnline: true
    },
    {
      id: 'conv4',
      name: 'Political Discussion',
      lastMessage: 'You: What do you think about the latest press conference?',
      timestamp: 'Monday',
      unreadCount: 0,
      isGroup: true
    },
    {
      id: 'conv5',
      name: 'Emily Rodriguez',
      avatar: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=150',
      lastMessage: 'Thanks for sharing that clip!',
      timestamp: 'Monday',
      unreadCount: 0,
      isGroup: false
    },
    {
      id: 'conv6',
      name: 'Tech News Club',
      lastMessage: 'Alex: Did anyone watch the product launch?',
      timestamp: 'Last week',
      unreadCount: 0,
      isGroup: true
    },
    {
      id: 'conv7',
      name: 'David Wilson',
      avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150',
      lastMessage: 'Let me know when you post the follow-up',
      timestamp: 'Last week',
      unreadCount: 0,
      isGroup: false
    }
  ]);
  
  const [messages, setMessages] = React.useState<Record<string, ChatMessage[]>>({
    conv1: [
      {
        id: 'm1',
        content: 'Hey there! Did you see the latest news clip about the tech conference?',
        sender: 'Sarah Johnson',
        senderAvatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150',
        timestamp: '10:15 AM',
        isOwn: false,
        isRead: true
      },
      {
        id: 'm2',
        content: 'Not yet! What happened?',
        sender: 'Me',
        timestamp: '10:17 AM',
        isOwn: true,
        isRead: true
      },
      {
        id: 'm3',
        content: 'They announced some incredible new AR tech. I think it would make a great topic for one of your videos!',
        sender: 'Sarah Johnson',
        senderAvatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150',
        timestamp: '10:20 AM',
        isOwn: false,
        isRead: true
      },
      {
        id: 'm4',
        content: 'Can you send me a link to the clip?',
        sender: 'Me',
        timestamp: '10:22 AM',
        isOwn: true,
        isRead: true
      },
      {
        id: 'm5',
        content: 'Sure! Here you go: https://example.com/tech-conference-highlights',
        sender: 'Sarah Johnson',
        senderAvatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150',
        timestamp: '10:25 AM',
        isOwn: false,
        isRead: true
      },
      {
        id: 'm6',
        content: 'Thanks! I\'ll definitely check it out. Maybe we can collaborate on a video about it?',
        sender: 'Me',
        timestamp: '10:30 AM',
        isOwn: true,
        isRead: false
      }
    ],
    conv3: [
      {
        id: 'm1',
        content: 'Hi there! I really enjoyed your latest video about the political debate.',
        sender: 'Michael Chen',
        senderAvatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150',
        timestamp: 'Yesterday, 3:45 PM',
        isOwn: false,
        isRead: true
      },
      {
        id: 'm2',
        content: 'Thanks Michael! I worked really hard on that one.',
        sender: 'Me',
        timestamp: 'Yesterday, 4:00 PM',
        isOwn: true,
        isRead: true
      },
      {
        id: 'm3',
        content: 'Your analysis was spot on. Looking forward to your next upload!',
        sender: 'Michael Chen',
        senderAvatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150',
        timestamp: 'Yesterday, 4:05 PM',
        isOwn: false,
        isRead: true
      },
      {
        id: 'm4',
        content: 'I\'m working on a follow-up about the economic implications. Should be up tomorrow!',
        sender: 'Me',
        timestamp: 'Yesterday, 4:10 PM',
        isOwn: true,
        isRead: true
      }
    ]
  });
  
  const [activeConversationId, setActiveConversationId] = React.useState<string | null>(null);
  const [searchTerm, setSearchTerm] = React.useState('');
  const messageContainerRef = React.useRef<HTMLDivElement>(null);
  
  // Check if screen is mobile-sized
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const isMobile = !isDesktop;
  
  // Scroll to bottom when messages change
  React.useEffect(() => {
    if (messageContainerRef.current) {
      messageContainerRef.current.scrollTop = messageContainerRef.current.scrollHeight;
    }
  }, [activeConversationId, messages]);
  
  const activeConversation = React.useMemo(() => {
    if (!activeConversationId) return null;
    return conversations.find(c => c.id === activeConversationId) || null;
  }, [conversations, activeConversationId]);
  
  const activeMessages = React.useMemo(() => {
    if (!activeConversationId) return [];
    return messages[activeConversationId] || [];
  }, [messages, activeConversationId]);
  
  const handleSendMessage = (messageText: string) => {
    if (!activeConversationId) return;
    
    const newMessage: ChatMessage = {
      id: `new-${Date.now()}`,
      content: messageText,
      sender: 'Me',
      timestamp: 'Just now',
      isOwn: true,
      isRead: false
    };
    
    setMessages(prev => ({
      ...prev,
      [activeConversationId]: [...(prev[activeConversationId] || []), newMessage]
    }));
    
    // Update last message in conversation list
    setConversations(prev =>
      prev.map(conv =>
        conv.id === activeConversationId
          ? { ...conv, lastMessage: `You: ${messageText}`, timestamp: 'Just now', unreadCount: 0 }
          : conv
      )
    );
    
    // Simulate reply for demo purposes
    if (activeConversationId === 'conv1') {
      setTimeout(() => {
        const replyMessage: ChatMessage = {
          id: `reply-${Date.now()}`,
          content: 'That sounds great! I\'d love to collaborate on a video about the new AR tech.',
          sender: 'Sarah Johnson',
          senderAvatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150',
          timestamp: 'Just now',
          isOwn: false,
          isRead: true
        };
        
        setMessages(prev => ({
          ...prev,
          [activeConversationId]: [...(prev[activeConversationId] || []), replyMessage]
        }));
        
        // Update conversation list
        setConversations(prev =>
          prev.map(conv =>
            conv.id === activeConversationId
              ? { ...conv, lastMessage: replyMessage.content, timestamp: 'Just now' }
              : conv
          )
        );
      }, 2000);
    }
  };
  
  // Mark messages as read when conversation is opened
  React.useEffect(() => {
    if (activeConversationId) {
      // Mark all messages as read
      setMessages(prev => {
        const conversationMessages = prev[activeConversationId] || [];
        const updatedMessages = conversationMessages.map(msg => ({
          ...msg,
          isRead: true
        }));
        
        return {
          ...prev,
          [activeConversationId]: updatedMessages
        };
      });
      
      // Reset unread count
      setConversations(prev =>
        prev.map(conv =>
          conv.id === activeConversationId
            ? { ...conv, unreadCount: 0 }
            : conv
        )
      );
    }
  }, [activeConversationId]);
  
  return (
    <div className={`fixed inset-0 bg-black overflow-hidden ${isDesktop ? 'pt-16 ml-[240px]' : ''}`}>
      <div className={`h-full flex ${isDesktop ? '' : 'pt-[64px] pb-16'}`}>
        {/* For mobile: show either sidebar or chat based on selection */}
        {isMobile ? (
          <>
            {!activeConversationId ? (
              <ChatSidebar
                conversations={conversations}
                activeConversationId={activeConversationId}
                onConversationSelect={setActiveConversationId}
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                onFilterToggle={() => {}}
                isMobile={isMobile}
              />
            ) : (
              <div className="flex flex-col h-full w-full">
                <ChatHeader 
                  conversation={activeConversation}
                  onBackClick={() => setActiveConversationId(null)}
                  isMobile={isMobile}
                />
                
                <div 
                  ref={messageContainerRef}
                  className="flex-1 overflow-y-auto p-4 custom-scrollbar"
                >
                  {activeMessages.map(message => (
                    <MessageBubble key={message.id} message={message} />
                  ))}
                </div>
                
                <ChatInput onSendMessage={handleSendMessage} />
              </div>
            )}
          </>
        ) : (
          // Desktop layout: sidebar + chat area
          <>
            <ChatSidebar
              conversations={conversations}
              activeConversationId={activeConversationId}
              onConversationSelect={setActiveConversationId}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              onFilterToggle={() => {}}
              isMobile={isMobile}
            />
            
            <div className="flex-1 flex flex-col h-full">
              {activeConversation ? (
                <>
                  <ChatHeader 
                    conversation={activeConversation}
                    onBackClick={() => {}}
                    isMobile={isMobile}
                  />
                  
                  <div 
                    ref={messageContainerRef}
                    className="flex-1 overflow-y-auto p-4 custom-scrollbar"
                  >
                    {activeMessages.map(message => (
                      <MessageBubble key={message.id} message={message} />
                    ))}
                  </div>
                  
                  <ChatInput onSendMessage={handleSendMessage} />
                </>
              ) : (
                <EmptyChatState />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}